import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "../components/footer";
import Header from "../components/header";
import SeoLinksSection from "../components/SeoLinksSection";
import { API_URL } from "./utils/utils";

function generateTimeOptions() {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 15, 30, 45]) {
      const h = String(hour).padStart(2, "0");
      const m = String(minute).padStart(2, "0");
      options.push(`${h}:${m}`);
    }
  }
  return options;
}

// Base URL for vehicle type images returned as `type_image` from get_vehicles API
const VEHICLE_IMAGE_BASE_URL = "https://api.aircabi.co.uk/public/assets/upload/";

export default function NewBooking() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tripType, setTripType] = useState("oneway");
  const [showPassengerPopup, setShowPassengerPopup] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [swapBtnHover, setSwapBtnHover] = useState(false);
  const [formData, setFormData] = useState({
    pickUp: "",
    dropOff: "",
    pickUpLat: "",
    pickUpLng: "",
    dropOffLat: "",
    dropOffLng: "",
    flightArrival: "",
    returnDate: "",
  });
  const [passengers, setPassengers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });
  const [luggage, setLuggage] = useState({
    large: 0,
    medium: 0,
  });
  const [carSeat, setCarSeat] = useState(0);
  const [showPickupDateTimePicker, setShowPickupDateTimePicker] = useState(false);
  const [showReturnDateTimePicker, setShowReturnDateTimePicker] = useState(false);
  const [pickupTempDate, setPickupTempDate] = useState("");
  const [pickupTempTime, setPickupTempTime] = useState("");
  const [returnTempDate, setReturnTempDate] = useState("");
  const [returnTempTime, setReturnTempTime] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [distanceMiles, setDistanceMiles] = useState("");
  const [durationMins, setDurationMins] = useState("");
  const [sortBy, setSortBy] = useState("default");

  const totalSelectedPassengers =
    passengers.adults + passengers.children + passengers.infants;

  const handleCloseValidation = () => {
    setValidationError("");
  };

  // Google Maps Places (key + script load, similar to Home5)
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceRef = useRef(null);
  const pickUpInputRef = useRef(null);
  const dropOffInputRef = useRef(null);
  const [pickUpSuggestions, setPickUpSuggestions] = useState([]);
  const [dropOffSuggestions, setDropOffSuggestions] = useState([]);
  const [pickUpDropdownRect, setPickUpDropdownRect] = useState(null);
  const [dropOffDropdownRect, setDropOffDropdownRect] = useState(null);

  const getTripLabelStyle = (isActive) => ({
    cursor: "pointer",
    fontSize: "16px",
    padding: "6px 18px",
    borderRadius: "999px",
    border: "1px solid #d70015",
    backgroundColor: isActive ? "#d70015" : "#ffffff",
    color: isActive ? "#ffffff" : "#333333",
    boxShadow: isActive ? "0 0 0 2px rgba(255, 7, 44, 0.35)" : "none",
    transition: "all 0.15s ease-in-out",
  });
  const flightArrivalInputRef = useRef(null);
  const returnDateInputRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);

  // Fetch Google Maps API key from backend API
  useEffect(() => {
    const fetchGoogleApiKey = async () => {
      try {
        const response = await fetch(`${API_URL}get_map_key/1`);
        if (!response.ok) {
          throw new Error("Failed to fetch Google Maps API key");
        }
        const data = await response.json();
        if (data && data.data && data.data.google_map_api_key) {
          setGoogleApiKey(data.data.google_map_api_key);
        }
      } catch (error) {
        console.error("Error fetching Google Maps API key (NewBooking):", error);
      }
    };

    fetchGoogleApiKey();
  }, []);

  // Load Google Maps script once we have the API key
  useEffect(() => {
    if (!googleApiKey) return;

    if (window.google && window.google.maps && window.google.maps.places) {
      setGoogleLoaded(true);
      return;
    }

    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.onload = () => setGoogleLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    document.body.appendChild(script);
  }, [googleApiKey]);

  const ensureAutocompleteService = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return null;
    }
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }
    return autocompleteServiceRef.current;
  };

  const ensurePlacesService = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return null;
    }
    if (!placesServiceRef.current) {
      const dummyDiv = document.createElement("div");
      placesServiceRef.current =
        new window.google.maps.places.PlacesService(dummyDiv);
    }
    return placesServiceRef.current;
  };

  const ensureDirections = () => {
    if (!window.google || !window.google.maps) {
      return null;
    }
    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }
    if (!directionsRendererRef.current) {
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer();
    }
    return {
      service: directionsServiceRef.current,
      renderer: directionsRendererRef.current,
    };
  };

  // Measure dropdown position for portal (so it appears above overlay/container)
  const measureDropdown = (field) => {
    const el = field === "pickUp" ? pickUpInputRef.current : dropOffInputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const style = {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
    if (field === "pickUp") setPickUpDropdownRect(style);
    else setDropOffDropdownRect(style);
  };

  useEffect(() => {
    if (pickUpSuggestions.length > 0) measureDropdown("pickUp");
    else setPickUpDropdownRect(null);
  }, [pickUpSuggestions]);

  useEffect(() => {
    if (dropOffSuggestions.length > 0) measureDropdown("dropOff");
    else setDropOffDropdownRect(null);
  }, [dropOffSuggestions]);

  useEffect(() => {
    if (pickUpSuggestions.length === 0 && dropOffSuggestions.length === 0) return;
    const onScrollOrResize = () => {
      if (pickUpSuggestions.length > 0) measureDropdown("pickUp");
      if (dropOffSuggestions.length > 0) measureDropdown("dropOff");
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [pickUpSuggestions.length, dropOffSuggestions.length]);

  // Initialize the map once Google is loaded
  useEffect(() => {
    if (!googleLoaded || !mapRef.current) return;
    if (mapInstanceRef.current) return;

    const center = {
      lat: parseFloat(formData.pickUpLat) || 51.5074, // default London
      lng: parseFloat(formData.pickUpLng) || -0.1278,
    };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      mapTypeId: 'roadmap', // Other options: 'satellite', 'hybrid', 'terrain'
      mapId: '56d16fa5b338c780', // Your Map ID
      zoom: 10,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: false,
      fullscreenControl: false,
        styles: [
            { featureType: "all", elementType: "all", stylers: [{ visibility: "off" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ visibility: "on" }] },
            { featureType: "road", elementType: "labels", stylers: [{ visibility: "on" }] }
        ]

    });
    mapInstanceRef.current = map;

    const dirs = ensureDirections();
    if (dirs) {
      dirs.renderer.setMap(map);
    }
  }, [googleLoaded, formData.pickUpLat, formData.pickUpLng]);

  // Helper to calculate route distance (miles) and duration (mins) using Google Directions
  const computeDistanceMiles = () => {
    return new Promise((resolve) => {
      if (
        !googleLoaded ||
        !mapInstanceRef.current ||
        !formData.pickUpLat ||
        !formData.pickUpLng ||
        !formData.dropOffLat ||
        !formData.dropOffLng
      ) {
        resolve(null);
        return;
      }

      const dirs = ensureDirections();
      if (!dirs) {
        resolve(null);
        return;
      }

      const origin = {
        lat: parseFloat(formData.pickUpLat),
        lng: parseFloat(formData.pickUpLng),
      };
      const destination = {
        lat: parseFloat(formData.dropOffLat),
        lng: parseFloat(formData.dropOffLng),
      };

      dirs.service.route(
        {
          origin,
          destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            dirs.renderer.setDirections(result);
            const legs = result.routes[0].legs;
            let meters = 0;
            let seconds = 0;
            legs.forEach((leg) => {
              if (leg.distance && typeof leg.distance.value === "number") {
                meters += leg.distance.value;
              }
              if (leg.duration && typeof leg.duration.value === "number") {
                seconds += leg.duration.value;
              }
            });
            const miles = meters / 1609.344;
            const durationMinutes = Math.round(seconds / 60);
            resolve({ miles, durationMinutes });
          } else {
            console.error("Directions request failed:", status);
            resolve(null);
          }
        }
      );
    });
  };

  // Keep distance in state for UI display whenever coordinates change
  useEffect(() => {
    let cancelled = false;

    const updateDistance = async () => {
      const result = await computeDistanceMiles();
      if (cancelled) return;
      if (result && typeof result.miles === "number") {
        setDistanceMiles(result.miles.toFixed(2));
        setDurationMins(
          Number.isFinite(result.durationMinutes)
            ? String(result.durationMinutes)
            : ""
        );
      } else {
        setDistanceMiles("");
        setDurationMins("");
      }
    };

    updateDistance();

    return () => {
      cancelled = true;
    };
  }, [
    googleLoaded,
    formData.pickUpLat,
    formData.pickUpLng,
    formData.dropOffLat,
    formData.dropOffLng,
  ]);

  // Cleanup popup on unmount
  useEffect(() => {
    return () => {
      setShowPassengerPopup(false);
    };
  }, []);

  // Load parameters from URL on mount
  useEffect(() => {
    const tripTypeParam = searchParams.get("tripType") || "oneway";
    const pickUpParam = searchParams.get("pickUp") || "";
    const dropOffParam = searchParams.get("dropOff") || "";
    const pickUpLatParam = searchParams.get("pickUpLat") || "";
    const pickUpLngParam = searchParams.get("pickUpLng") || "";
    const dropOffLatParam = searchParams.get("dropOffLat") || "";
    const dropOffLngParam = searchParams.get("dropOffLng") || "";
    const flightArrivalParam = searchParams.get("flightArrival") || "";
    const returnDateParam = searchParams.get("returnDate") || "";
    
    if (pickUpParam || dropOffParam) {
      setTripType(tripTypeParam);
      setFormData({
        pickUp: pickUpParam,
        dropOff: dropOffParam,
        pickUpLat: pickUpLatParam,
        pickUpLng: pickUpLngParam,
        dropOffLat: dropOffLatParam,
        dropOffLng: dropOffLngParam,
        flightArrival: flightArrivalParam,
        returnDate: returnDateParam,
      });
      setPassengers({
        adults: parseInt(searchParams.get("adults") || "1"),
        children: parseInt(searchParams.get("children") || "0"),
        infants: parseInt(searchParams.get("infants") || "0"),
      });
      setLuggage({
        large: parseInt(searchParams.get("largeLuggage") || "0"),
        medium: parseInt(searchParams.get("mediumLuggage") || "0"),
      });
      setCarSeat(parseInt(searchParams.get("carSeat") || "0", 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePassengerChange = (type, action) => {
    setPassengers((prev) => {
      const newValue = { ...prev };
      if (action === "increment") {
        newValue[type]++;
      } else if (action === "decrement" && newValue[type] > 0) {
        if (type === "adults" && newValue[type] > 1) {
          newValue[type]--;
        } else if (type !== "adults") {
          newValue[type]--;
        }
      }
      return newValue;
    });
  };

  const getPassengerDisplay = () => {
    const parts = [];
    if (passengers.adults > 0) {
      parts.push(String(passengers.adults));
    }
    if (passengers.children > 0) {
      parts.push(`${passengers.children} Child${passengers.children > 1 ? "ren" : ""}`);
    }
    if (passengers.infants > 0) {
      parts.push(`${passengers.infants} Infant${passengers.infants > 1 ? "s" : ""}`);
    }
    return parts.length > 0 ? parts.join(", ") : "Select Passengers";
  };

  const handleLocationChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (!googleLoaded || !value) {
      if (field === "pickUp") setPickUpSuggestions([]);
      if (field === "dropOff") setDropOffSuggestions([]);
      return;
    }

    const service = ensureAutocompleteService();
    if (!service) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // No types filter = all place types (addresses + airports + establishments)
      service.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: "gb" }, // Restrict to UK
        },
        (predictions, status) => {
          if (
            status !==
              window.google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            if (field === "pickUp") setPickUpSuggestions([]);
            if (field === "dropOff") setDropOffSuggestions([]);
            return;
          }

          const items = predictions.map((p) => {
            const types = p.types || [];
            const sf = p.structured_formatting || {};
            const mainText = sf.main_text || p.description || "";
            const secondaryText = sf.secondary_text || "";
            const desc = p.description || "";
            const isAirport = types.includes("airport") || /airport(?!\s+(terminal|term))/i.test(desc);
            const isTerminal = types.includes("transit_station") || /terminal|term\s*\d/i.test(desc);
            let category = "Address";
            if (isAirport && !isTerminal) category = "Airport";
            else if (isTerminal) category = "Terminal";
            else if (types.some((t) => ["establishment", "point_of_interest"].includes(t))) category = "Place";

            return {
              description: p.description,
              placeId: p.place_id,
              mainText,
              secondaryText,
              types,
              category,
            };
          });

          if (field === "pickUp") setPickUpSuggestions(items);
          if (field === "dropOff") setDropOffSuggestions(items);
        }
      );
    }, 300);
  };

  // Group suggestions by category for styled dropdown (Airport, Terminal, Place, Address)
  const groupSuggestionsByCategory = (suggestions) => {
    const order = ["Airport", "Terminal", "Place", "Address"];
    const groups = {};
    order.forEach((cat) => (groups[cat] = []));
    suggestions.forEach((s) => {
      const cat = s.category || "Address";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    return order.filter((cat) => groups[cat]?.length > 0).map((cat) => ({ category: cat, items: groups[cat] }));
  };

  const handleSelectSuggestion = (field, suggestion) => {
    const placesService = ensurePlacesService();
    if (!placesService) {
      console.error("PlacesService not available.");
      return;
    }

    placesService.getDetails(
      {
        placeId: suggestion.placeId,
        fields: ["geometry", "formatted_address"],
      },
      (place, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place ||
          !place.geometry ||
          !place.geometry.location
        ) {
          console.error("Failed to get place details:", status);
          return;
        }

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setFormData((prev) => ({
          ...prev,
          [field]: place.formatted_address || suggestion.description,
          [`${field}Lat`]: lat.toString(),
          [`${field}Lng`]: lng.toString(),
        }));

        // Clear BOTH suggestion lists to prevent DOM conflicts
        setPickUpSuggestions([]);
        setDropOffSuggestions([]);
      }
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "pickUp" || name === "dropOff") {
      handleLocationChange(name, value);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Clear location function
  const handleClearLocation = (field) => {
    if (field === "pickUp") {
      setFormData((prev) => ({
        ...prev,
        pickUp: "",
        pickUpLat: "",
        pickUpLng: "",
      }));
      setPickUpSuggestions([]);
    } else if (field === "dropOff") {
      setFormData((prev) => ({
        ...prev,
        dropOff: "",
        dropOffLat: "",
        dropOffLng: "",
      }));
      setDropOffSuggestions([]);
    }
  };

  // Helpers to manage date + time as a single ISO-like string (YYYY-MM-DDTHH:mm)
  const handleDateTimeChange = (field, date, time) => {
    setFormData((prev) => {
      const combined = date && time ? `${date}T${time}` : "";
      return {
        ...prev,
        [field]: combined,
      };
    });
  };

  const getDatePart = (value) => {
    if (!value) return "";
    const [date] = value.split("T");
    return date || "";
  };

  const getTimePart = (value) => {
    if (!value) return "";
    const [, time] = value.split("T");
    return time || "";
  };

  const formatDisplayDateTime = (value) => {
    if (!value) return "";
    const [date, time] = value.split("T");
    if (!date) return "";
    const [year, month, day] = date.split("-");
    const datePart = `${day}/${month}/${year}`;
    return time ? `${datePart} ${time}` : datePart;
  };

  const buildDateFromParts = (date, time) => {
    if (!date || !time) return null;
    const combined = `${date}T${time}`;
    const d = new Date(combined);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  // Swap locations function
  const handleSwapLocations = () => {
    setFormData((prev) => ({
      ...prev,
      pickUp: prev.dropOff,
      dropOff: prev.pickUp,
      pickUpLat: prev.dropOffLat,
      pickUpLng: prev.dropOffLng,
      dropOffLat: prev.pickUpLat,
      dropOffLng: prev.pickUpLng,
    }));
    setPickUpSuggestions([]);
    setDropOffSuggestions([]);
  };

  // Function to get sorted results based on sortBy
  const getSortedResults = () => {
    if (sortBy === "default") {
      return searchResults;
    }
    const sorted = [...searchResults].sort((a, b) => {
      const priceA = Number(a.total_charges) || 0;
      const priceB = Number(b.total_charges) || 0;
      if (sortBy === "lowest") {
        return priceA - priceB;
      } else if (sortBy === "highest") {
        return priceB - priceA;
      }
      return 0;
    });
    return sorted;
  };

  // API function to search bookings
  const searchBookings = async (milesFromCaller) => {
    try {
      const miles = milesFromCaller || distanceMiles || "0";

      const response = await fetch(`${API_URL}get_vehicles/1/${miles}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("get_vehicles response:", data);

      // Ensure we always return an array
      if (!data || data.status === false || !Array.isArray(data.data)) {
        return [];
      }

      return data.data;
    } catch (error) {
      console.error("Search error:", error);
      return [];
    }
  };

  // Auto-search on page load once we have a distance and locations
  useEffect(() => {
    const shouldAutoSearch =
      distanceMiles &&
      formData.pickUp &&
      formData.dropOff;

    if (!shouldAutoSearch) return;

    let cancelled = false;

    const run = async () => {
      try {
        const results = await searchBookings(distanceMiles);
        if (!cancelled) {
          setSearchResults(results);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Auto search failed:", err);
          setSearchResults([]);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [distanceMiles, formData.pickUp, formData.dropOff]);

  // Handler for search button click - validate round trip return date/time, then show loader briefly
  const handleSearchClick = () => {
    // Validate pickup not in the past
    if (formData.flightArrival) {
      const dep = new Date(formData.flightArrival).getTime();
      const now = Date.now();
      if (!Number.isNaN(dep) && dep < now) {
        setValidationError("Pickup date/time cannot be in the past.");
        return;
      }
    }

    // Validate return relationship when both set
    if (formData.flightArrival && formData.returnDate) {
      const dep = new Date(formData.flightArrival).getTime();
      const ret = new Date(formData.returnDate).getTime();
      if (!Number.isNaN(dep) && !Number.isNaN(ret) && ret <= dep) {
        setValidationError("Return date/time must be after pickup date/time.");
        return;
      }
    }

    // Validate return date for Round trip (similar to home5.js)
    if (tripType === "Round" && !formData.returnDate) {
      setValidationError("Return date is required for round trip.");
      return;
    }

    // Hide results and show loader
    setLoading(true);

    // After 2 seconds, show results again
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await handleSearchClick();
  };

  const handleLuggageChange = (type, action) => {
    setLuggage((prev) => {
      const newValue = { ...prev };
      if (action === "increment") {
        newValue[type]++;
      } else if (action === "decrement" && newValue[type] > 0) {
        newValue[type]--;
      }
      return newValue;
    });
  };

  const handleCarSeatChange = (action) => {
    setCarSeat((prev) => {
      if (action === "increment") return prev + 1;
      if (action === "decrement" && prev > 0) return prev - 1;
      return prev;
    });
  };

  const handleBookNow = (result) => {
    // Validate return date for Round trip before proceeding to confirmation
    if (tripType === "Round" && !formData.returnDate) {
      setValidationError("Return date is required for round trip.");
      return;
    }

    const basePrice = Number(result.total_charges) || 0;
    const multiplier = tripType === "Round" ? 2 : 1;
    const totalPrice = (basePrice * multiplier).toFixed(2);

    const bookingParams = new URLSearchParams({
      tripType,
      pickUp: formData.pickUp,
      dropOff: formData.dropOff,
      pickUpLat: formData.pickUpLat || "",
      pickUpLng: formData.pickUpLng || "",
      dropOffLat: formData.dropOffLat || "",
      dropOffLng: formData.dropOffLng || "",
      flightArrival: formData.flightArrival,
      returnDate: formData.returnDate || "",
      adults: passengers.adults,
      children: passengers.children,
      infants: passengers.infants,
      // Luggage details
      largeLuggage: luggage.large,
      mediumLuggage: luggage.medium,
      carSeat: carSeat,
      // Vehicle details
      vehicleTypeId: result.vehicle_type_id,
      vehicleTypeName: result.type_name,
      vehiclePassengers: result.passengers,
      vehicleSmallLuggage: result.small_luggage,
      vehicleMediumLuggage: result.medium_luggage,
      vehicleLargeLuggage: result.large_luggage,
      vehicleTotalCharges: result.total_charges,
      vehicleMiles: result.miles,
      distanceMiles: distanceMiles || "",
      // Generic service-style fields for confirmation page
      serviceId: result.vehicle_type_id,
      serviceTitle: result.type_name,
      servicePrice: basePrice.toFixed(2),
      serviceLocation: `${formData.pickUp} → ${formData.dropOff}`,
      totalPrice,
    });
    navigate(`/booking_confirmation?${bookingParams.toString()}`);
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      {validationError && (
        <div
          className="custom-alert-overlay"
          onClick={handleCloseValidation}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            zIndex: 2300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="custom-alert box-shadow bg-white rounded p-4"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "420px",
              width: "90%",
              textAlign: "center",
            }}
          >
            <div className="mb-3 d-flex justify-content-center align-items-center">
              <span
                style={{
                  display: "inline-flex",
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,193,7,0.15)",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fa fa-exclamation-triangle"
                  style={{ color: "#ffc107", fontSize: "28px" }}
                />
              </span>
            </div>
            <h4 className="mb-2">Warning</h4>
            <p className="mb-4" style={{ fontSize: "14px" }}>
              {validationError}
            </p>
            <button
              type="button"
              className="nir-btn w-100"
              onClick={handleCloseValidation}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <Header />
      <>
        {/* BreadCrumb Starts */}
       
        {/* BreadCrumb Ends */}

        {/* Search Form Section */}
        <section className="trending pt-1 pb-0 bg-lgrey">
          <div className="container">
            <div className="bg-white rounded p-4 mb-1" style={{ border: "1px solid #d70015" }}>


              <form onSubmit={handleSearch}>
                {/* Trip Type Radio Buttons */}
                <div className="row mb-1">
                  <div className="col-12 d-flex justify-content-center align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <div className="form-check" style={{ paddingLeft: 0 }}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="tripType"
                          id="oneway"
                          value="oneway"
                          checked={tripType === "oneway"}
                          onChange={(e) => {
                            setTripType(e.target.value);
                            setFormData((prev) => ({
                              ...prev,
                              returnDate: "",
                            }));
                          }}
                          style={{
                            position: "absolute",
                            opacity: 0,
                            pointerEvents: "none",
                          }}
                        />
                        <label
                          className="form-check-label fw-bold ms-0 me-2"
                          htmlFor="oneway"
                          style={getTripLabelStyle(tripType === "oneway")}
                        >
                          One Way
                        </label>
                      </div>
                      <div className="form-check" style={{ paddingLeft: 0 }}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="tripType"
                          id="roundtrip"
                          value="Round"
                          checked={tripType === "Round"}
                          onChange={(e) => setTripType(e.target.value)}
                          style={{
                            position: "absolute",
                            opacity: 0,
                            pointerEvents: "none",
                          }}
                        />
                        <label
                          className="form-check-label fw-bold ms-0"
                          htmlFor="roundtrip"
                          style={getTripLabelStyle(tripType === "Round")}
                        >
                          Round Trip
                        </label>
                      </div>
                    </div>
                    </div>

                </div>

                {/* Form Fields - centered; one way: wider pickup/dropoff; round: one line, narrower passengers, wider pickup/dropoff */}
                <div className="d-flex justify-content-center">
                <div className={`row g-2 align-items-end justify-content-center ${tripType === "Round" ? "flex-nowrap" : ""}`} style={{ width: "100%", maxWidth: "100%" }}>
                  {/* Pick Up - wider when one way or round */}
                  <div className={tripType === "Round" ? "col-lg-3 col-md-6" : "col-lg-3 col-md-6"}>
                    <div className="form-group position-relative mb-0" style={{ zIndex: 1050 }}>
                      <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Pick Up</label>
                      <input
                        ref={pickUpInputRef}
                        type="text"
                        name="pickUp"
                        value={formData.pickUp}
                        onChange={handleInputChange}
                        className="form-control border"
                        placeholder="Enter pick up location"
                        style={{ borderRadius: "4px", padding: "7px 13px", fontSize: "1rem", height: "42px" }}
                        required
                      />
                      {formData.pickUp && (
                        <button
                          type="button"
                          className="theme close-circle"
                          onClick={() => handleClearLocation("pickUp")}
                          style={{
                            position: "absolute",
                            right: "3px",
                            top: "calc(60% + 10px)",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            color: "#777",
                            cursor: "pointer",
                          }}
                          aria-label="Clear pick up location"
                        >
                          <i
                            className="fa fa-times-circle"
                            style={{ fontSize: "18px" }}
                          />
                        </button>
                      )}
                      {googleLoaded && pickUpSuggestions.length > 0 && pickUpDropdownRect &&
                        createPortal(
                          <div
                            className="places-suggestion-list places-suggestion-dropdown"
                            style={{
                              position: "fixed",
                              top: pickUpDropdownRect.top,
                              left: pickUpDropdownRect.left,
                              width: pickUpDropdownRect.width,
                              zIndex: 99999,
                              maxHeight: "280px",
                              overflowY: "auto",
                              backgroundColor: "#fff",
                              margin: 0,
                              borderRadius: "8px",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                              border: "1px solid #e8e8e8",
                              padding: "6px 0",
                            }}
                          >
                            {groupSuggestionsByCategory(pickUpSuggestions).map(({ category, items }) => (
                              <div key={category} className="places-suggestion-group">
                                <div className="places-suggestion-group-header">
                                  <i className={category === "Airport" ? "fa fa-plane" : category === "Terminal" ? "fa fa-building" : category === "Place" ? "fa fa-map-marker-alt" : "fa fa-map-pin"} />
                                  <span>{category}</span>
                                </div>
                                {items.map((s) => (
                                  <div
                                    key={s.placeId}
                                    className="places-suggestion-item"
                                    onClick={() => handleSelectSuggestion("pickUp", s)}
                                  >
                                    <span className="places-suggestion-main">{s.mainText || s.description}</span>
                                    {s.secondaryText && <span className="places-suggestion-secondary">{s.secondaryText}</span>}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>

                  {/* Swap Locations Button */}
                  <div className="col-lg-auto col-md-6 d-flex justify-content-center align-items-end" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                    <button
                      type="button"
                      className="btn rounded-circle border"
                      onClick={handleSwapLocations}
                      onMouseEnter={() => setSwapBtnHover(true)}
                      onMouseLeave={() => setSwapBtnHover(false)}
                      style={{
                        width: "42px",
                        height: "42px",
                        padding: 0,
                        marginBottom: "0px",
                        backgroundColor: swapBtnHover ? "#fff" : "#d70015",
                        borderColor: "#d70015",
                        color: swapBtnHover ? "#d70015" : "#fff",
                      }}
                      aria-label="Swap Pick Up and Drop Off"
                    >
                      <i className="fa fa-exchange-alt" />
                    </button>
                  </div>

                  {/* Drop Off - wider when one way or round */}
                  <div className={tripType === "Round" ? "col-lg-3 col-md-6" : "col-lg-3 col-md-6"}>
                    <div className="form-group position-relative mb-0" style={{ zIndex: 1050 }}>
                      <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Drop Off</label>
                      <input
                        ref={dropOffInputRef}
                        type="text"
                        name="dropOff"
                        value={formData.dropOff}
                        onChange={handleInputChange}
                        className="form-control border"
                        placeholder="Enter drop off location"
                        style={{ borderRadius: "4px", padding: "7px 13px", fontSize: "1rem", height: "42px" }}
                        required
                      />
                      {formData.dropOff && (
                        <button
                          type="button"
                          className="theme close-circle"
                          onClick={() => handleClearLocation("dropOff")}
                          style={{
                            position: "absolute",
                            right: "3px",
                            top: "calc(60% + 10px)",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            color: "#777",
                            cursor: "pointer",
                          }}
                          aria-label="Clear drop off location"
                        >
                          <i
                            className="fa fa-times-circle"
                            style={{ fontSize: "18px" }}
                          />
                        </button>
                      )}
                      {googleLoaded && dropOffSuggestions.length > 0 && dropOffDropdownRect &&
                        createPortal(
                          <div
                            className="places-suggestion-list places-suggestion-dropdown"
                            style={{
                              position: "fixed",
                              top: dropOffDropdownRect.top,
                              left: dropOffDropdownRect.left,
                              width: dropOffDropdownRect.width,
                              zIndex: 99999,
                              maxHeight: "280px",
                              overflowY: "auto",
                              backgroundColor: "#fff",
                              margin: 0,
                              borderRadius: "8px",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                              border: "1px solid #e8e8e8",
                              padding: "6px 0",
                            }}
                          >
                            {groupSuggestionsByCategory(dropOffSuggestions).map(({ category, items }) => (
                              <div key={category} className="places-suggestion-group">
                                <div className="places-suggestion-group-header">
                                  <i className={category === "Airport" ? "fa fa-plane" : category === "Terminal" ? "fa fa-building" : category === "Place" ? "fa fa-map-marker-alt" : "fa fa-map-pin"} />
                                  <span>{category}</span>
                                </div>
                                {items.map((s) => (
                                  <div
                                    key={s.placeId}
                                    className="places-suggestion-item"
                                    onClick={() => handleSelectSuggestion("dropOff", s)}
                                  >
                                    <span className="places-suggestion-main">{s.mainText || s.description}</span>
                                    {s.secondaryText && <span className="places-suggestion-secondary">{s.secondaryText}</span>}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>

                  {/* Flight Arrival (single field with custom date+time picker) */}
                  <div className={tripType === "Round" ? "col-lg-2 col-md-6" : "col-lg-2 col-md-6"}>
                    <div className="form-group mb-0">
                      <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Flight Arrival</label>
                      <input
                        ref={flightArrivalInputRef}
                        type="text"
                        name="flightArrival"
                        readOnly
                        value={formatDisplayDateTime(formData.flightArrival)}
                        onClick={() => {
                          setPickupTempDate(getDatePart(formData.flightArrival));
                          setPickupTempTime(getTimePart(formData.flightArrival));
                          setShowPickupDateTimePicker(true);
                        }}
                        className="form-control"
                        placeholder="Select date, on mm/dd/yyyy"
                        style={{
                          borderRadius: "4px",
                          padding: "7px 13px",
                          fontSize: "1rem",
                          height: "42px",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          backgroundColor: "#ffffff",
                          border: "1px solid #d70015",
                          color: formData.flightArrival ? "#212529" : "#d70015",
                        }}
                        required
                      />
                    </div>
                  </div>

                  {/* Return Date (enabled only for Round Trip, single field with custom picker) */}
                  {tripType === "Round" && (
                    <div className="col-lg-2 col-md-6">
                      <div className="form-group mb-0">
                        <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Return Date</label>
                        <input
                          ref={returnDateInputRef}
                          type="text"
                          name="returnDate"
                          readOnly
                          value={formatDisplayDateTime(formData.returnDate)}
                          onClick={() => {
                            setReturnTempDate(getDatePart(formData.returnDate));
                            setReturnTempTime(getTimePart(formData.returnDate));
                            setShowReturnDateTimePicker(true);
                          }}
                          className="form-control"
                          placeholder="Select date"
                          style={{
                            borderRadius: "4px",
                            padding: "7px 13px",
                            fontSize: "1rem",
                            height: "42px",
                            backgroundColor: "#ffffff",
                            boxSizing: "border-box",
                            cursor: "pointer",
                            border: "1px solid #d70015",
                            color: formData.returnDate ? "#212529" : "#d70015",
                          }}
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Passengers - narrower when round trip */}
                  <div className={tripType === "Round" ? "col-lg-1 col-md-6" : "col-lg-2 col-md-6"} style={tripType === "Round" ? { minWidth: "100px" } : undefined}>
                    <div className="form-group position-relative mb-0">
                      <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Passengers</label>
                      <div
                        className="form-control border"
                        onClick={() => setShowPassengerPopup(!showPassengerPopup)}
                        style={{
                          cursor: "pointer",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "7px 13px",
                          fontSize: "1rem",
                          height: "42px",
                        }}
                      >
                        <span>{getPassengerDisplay()}</span>
                        <i className="fa fa-chevron-down ms-2"></i>
                      </div>

                      {/* Passenger Popup */}
                      {showPassengerPopup && (
                        <div
                          className="bg-white border rounded shadow-lg p-4"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1050,
                            marginTop: "8px",
                            minWidth: "280px",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Adults */}
                          <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                            <div>
                              <div className="fw-bold">Passenger</div>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                              <button
                                type="button"
                                className="btn btn-outline-secondary rounded-circle"
                        style={{
                          width: "35px",
                          height: "35px",
                          padding: 0,
                          border: "1px solid #d70015",
                          color: "#d70015",
                        }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePassengerChange("adults", "decrement");
                                }}
                                disabled={passengers.adults <= 1}
                              >
                                <i className="fa fa-minus"></i>
                              </button>
                              <span
                                className="fw-bold"
                                style={{ minWidth: "30px", textAlign: "center" }}
                              >
                                {passengers.adults}
                              </span>
                              <button
                                type="button"
                                className="btn btn-outline-secondary rounded-circle"
                        style={{
                          width: "35px",
                          height: "35px",
                          padding: 0,
                          border: "1px solid #d70015",
                          color: "#d70015",
                        }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePassengerChange("adults", "increment");
                                }}
                              >
                                <i className="fa fa-plus"></i>
                              </button>
                            </div>
                          </div>

                          <div className="text-end">
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{
                                backgroundColor: "#d70015",
                                borderColor: "#d70015",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPassengerPopup(false);
                              }}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Button */}
                  <div className="col-lg-1 col-md-6">
                    <div className="form-group mb-0">
                      <label className="fw-bold mb-1 d-block" style={{ fontSize: "1rem", visibility: "hidden" }}>Search</label>
                      <span
                        id="search-vehicles-btn"

                        className="nir-btn w-100 rounded overflow-visible"
                        style={{ 
                          fontSize: "1rem", 
                          fontWeight: "300",
                          padding: "10px 6px",
                          height: "42px",
                          display: "inline-block",
                          cursor: "pointer"
                        }}
                        onClick={handleSearchClick}
                      >
                        <i className="fa fa-search mr-2" />
                      </span>
                    </div>
                  </div>

                  {/* Hidden distance field for form completeness / debugging */}
                  <input
                    type="hidden"
                    name="distanceMiles"
                    value={distanceMiles || ""}
                  />
                </div>
                </div>
              </form>
            </div>

            {/* Map + Search Results */}
            <div className="row">
                <div className="col-lg-8" id="results">
                    <div className="list-results d-flex align-items-center justify-content-between mb-0">
                        <div className="list-results-sort">
                            <p className="m-0">
                                {loading
                                    ? "Searching..."
                                    : `Showing ${searchResults.length} results`}
                            </p>
                        </div>
                        {!loading && searchResults.length > 0 && (
                            <div className="sortby d-flex align-items-center">
                                <label className="me-2 mb-0">Sort by:</label>
                                <select
                                    className="form-select"
                                    style={{ width: "auto", minWidth: "180px" }}
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    <option value="default">Default</option>
                                    <option value="lowest">Price: Lowest to Highest</option>
                                    <option value="highest">Price: Highest to Lowest</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div
                            style={{
                                position: "relative",
                                width: "100%",
                                minHeight: "400px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#f4f4f4",
                                borderRadius: "8px",
                            }}
                        >
                            <div
                                style={{
                                    width: "400px",
                                    height: "300px",
                                    background: "url(/images/loader.gif)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                }}
                            />
                        </div>
                    ) : searchResults.length > 0 ? (
                    <div className="react-search-results">
                      {getSortedResults().map((result) => (
                        <div
                          key={result.vehicle_type_id}
                          className="bg-white rounded border p-4 mb-3"
                          style={{ borderColor: "#d70015" }}
                        >
                          <div className="row align-items-center">
                            {/* Vehicle image */}
                            <div className="col-md-4 mb-3 mb-md-0 d-flex align-items-center justify-content-center">
                              <img
                                src={
                                  result.type_image
                                    ? `${VEHICLE_IMAGE_BASE_URL}${result.type_image}`
                                    : "images/trending/aircabi_mini.png"
                                }
                                alt={result.type_name}
                                className="img-fluid rounded"
                              />
                            </div>

                            {/* Vehicle info */}
                            <div className="col-md-5 mb-3 mb-md-0">
                              <div className="text-md-start text-center">
                                <h3 className="mb-1">{result.type_name}</h3>
                                {result.category_name && (
                                  <p className="text-muted mb-2">
                                    {result.category_name}
                                  </p>
                                )}

                                {/* Passengers (icon + count, text on hover) */}
                                <p className="mb-1 d-flex align-items-center justify-content-md-start justify-content-center gap-2">
                                  <span
                                      className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                      style={{
                                          width: 32,
                                          height: 32,
                                          backgroundColor: "#fbe9ea",
                                      }}
                                      title="Cab images are for reference only. Actual cab type depends on passenger and luggage capacity."
                                  >
                                    <i className="fa fa-info-circle" style={{ color: "#d70015" }} />
                                  </span>

                                    <span
                                        style={{ marginLeft: 6, fontSize: 13, color: "#555" }}
                                        title="Cab images are for reference only. Actual cab type depends on passenger and luggage capacity."
                                    >
                                      Actual cab type depends on passenger and luggage capacity.
                                    </span>

                                </p>

                                {/* Passengers and luggage icons */}
                                <p className="mb-0 d-flex align-items-center justify-content-md-start justify-content-center gap-4">
                                  <span
                                      className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                      style={{
                                          width: 60,
                                          height: 32,
                                          backgroundColor: "#f5f5f5",
                                      }}
                                      title={`Passengers: ${totalSelectedPassengers} / ${result.passengers}`}
                                  >
                                    <i className="fa fa-user-friends theme" />

                                      <span style={{ marginLeft: 8 }} >{result.passengers}</span>
                                  </span>


                                    <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle px-2"
                                    style={{
                                      height: 32,
                                      backgroundColor: "#f5f5f5",
                                      fontSize: 13,
                                    }}
                                    title={`Medium bags: ${result.medium_luggage}`}
                                  >
                                    <i className="fa fa-suitcase-rolling theme" />
                                    <span style={{ marginLeft: 6 }}>{result.medium_luggage}</span>
                                  </span>
                                  <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle px-2"
                                    style={{
                                      height: 34,
                                      backgroundColor: "#f5f5f5",
                                      fontSize: 14,
                                    }}
                                    title={`Large bags: ${result.large_luggage}`}
                                  >
                                    <i className="fa fa-suitcase theme" />
                                    <span style={{ marginLeft: 6 }}>{result.large_luggage}</span>
                                  </span>
                                </p>
                              </div>
                            </div>

                            {/* Price + CTA */}
                            <div className="col-md-3 d-flex flex-column align-items-md-end align-items-center justify-content-center">
                              <div className="mb-2 text-md-end text-center">
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: "#d70015",
                                    fontSize: "1.25rem",
                                    display: "block",
                                  }}
                                >
                                  £{Number(result.total_charges).toFixed(2)}
                                </span>
                                {tripType === "Round" && (
                                  <span className="d-block text-muted" style={{ fontSize: "0.875rem" }}>
                                    Each Trip Charges
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="nir-btn w-100"
                                onClick={() => handleBookNow(result)}
                              >
                                Book Now
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    ) : formData.pickUp && formData.dropOff ? (
                        <div
                            style={{
                                position: "relative",
                                width: "100%",
                                minHeight: "400px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#f4f4f4",
                                borderRadius: "8px",
                            }}
                        >
                            <div
                                style={{
                                    width: "400px",
                                    height: "300px",
                                    background: "url(/images/loader.gif)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                }}
                            />
                        </div>
                    ) : null}
                </div>
              <div className="col-lg-4 mb-4">
                  <div id={'rout_map'}
                       ref={mapRef}
                       style={{
                           width: "100%",
                           height: "320px",
                           borderRadius: "8px",
                           overflow: "hidden",
                       }}
                       className="border"
                  />
                  <div className="mt-3 bg-white rounded p-3" style={{ border: "1px solid #d70015" }}>

                      <h5 className="mb-3">
                          <i className="icon-directions theme me-2" />
                          Your Journey
                      </h5>
                      {/* Pick Up */}
                      <div className="mb-2 d-flex align-items-start">
                          <i className="fa fa-map-marker-alt me-2 mt-1" style={{ color: "#28a745" }} />
                          <div>{formData.pickUp || "-"}</div>
                      </div>
                      {/* Departure Date/Time */}
                      <div className="mb-2 d-flex align-items-start">
                          <i className="fa fa-calendar-alt me-2 mt-1 theme" />
                          <div>{formatDateTime(formData.flightArrival)}</div>
                      </div>

                      {/* Estimated Duration */}
                      <div className="mb-2 d-flex align-items-start ps-3">
                          <i className="fa fa-clock me-2 mt-1 theme" />
                          <div>
                              Estimated duration: {durationMins ? `${durationMins} mins` : "-"}
                          </div>
                      </div>
                      {/* Drop Off */}
                      <div className="d-flex align-items-start">
                          <i className="fa fa-map-marker me-2 mt-1 text-danger" />
                          <div>{formData.dropOff || "-"}</div>
                      </div>
                  </div>

                  {/* Luggage Selection */}
                  <div className="mt-3 bg-white rounded p-3" style={{ border: "1px solid #d70015" }}>
                      <h5 className="mb-3">
                          <i className="fa fa-suitcase theme me-2" />
                          Luggage Selection
                      </h5>
                      
                      {/* Medium Luggage */}
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                          <div>
                              <div className="fw-bold" style={{ fontSize: "1rem" }}>Medium Luggage</div>
                              <small className="text-muted" style={{ fontSize: "0.8rem" }}>Medium sized bags</small>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                              <button
                                  type="button"
                                  className="btn btn-outline-secondary rounded-circle"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #d70015",
                                    color: "#d70015",
                                  }}
                                  onClick={() => handleLuggageChange("medium", "decrement")}
                                  disabled={luggage.medium <= 0}
                              >
                                  <i className="fa fa-minus" style={{ fontSize: "0.8rem" }}></i>
                              </button>
                              <span className="fw-bold" style={{ minWidth: "25px", textAlign: "center", fontSize: "1rem" }}>
                                  {luggage.medium}
                              </span>
                              <button
                                  type="button"
                                  className="btn btn-outline-secondary rounded-circle"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #d70015",
                                    color: "#d70015",
                                  }}
                                  onClick={() => handleLuggageChange("medium", "increment")}
                              >
                                  <i className="fa fa-plus" style={{ fontSize: "0.8rem" }}></i>
                              </button>
                          </div>
                      </div>

                      {/* Large Luggage */}
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                          <div>
                              <div className="fw-bold" style={{ fontSize: "1rem" }}>Large Luggage</div>
                              <small className="text-muted" style={{ fontSize: "0.8rem" }}>Large sized bags</small>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                              <button
                                  type="button"
                                  className="btn btn-outline-secondary rounded-circle"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #d70015",
                                    color: "#d70015",
                                  }}
                                  onClick={() => handleLuggageChange("large", "decrement")}
                                  disabled={luggage.large <= 0}
                              >
                                  <i className="fa fa-minus" style={{ fontSize: "0.8rem" }}></i>
                              </button>
                              <span className="fw-bold" style={{ minWidth: "25px", textAlign: "center", fontSize: "1rem" }}>
                                  {luggage.large}
                              </span>
                              <button
                                  type="button"
                                  className="btn btn-outline-secondary rounded-circle"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #d70015",
                                    color: "#d70015",
                                  }}
                                  onClick={() => handleLuggageChange("large", "increment")}
                              >
                                  <i className="fa fa-plus" style={{ fontSize: "0.8rem" }}></i>
                              </button>
                          </div>
                      </div>

                      {/* Child Seat */}
                      <div className="d-flex justify-content-between align-items-center">
                          <div>
                              <div className="fw-bold" style={{ fontSize: "1rem" }}>Child Seat</div>
                              <small className="text-muted" style={{ fontSize: "0.8rem" }}>Child car seat</small>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                              <button
                                  type="button"
                                  className="btn btn-outline-secondary rounded-circle"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #d70015",
                                    color: "#d70015",
                                  }}
                                  onClick={() => handleCarSeatChange("decrement")}
                                  disabled={carSeat <= 0}
                              >
                                  <i className="fa fa-minus" style={{ fontSize: "0.8rem" }}></i>
                              </button>
                              <span className="fw-bold" style={{ minWidth: "25px", textAlign: "center", fontSize: "1rem" }}>
                                  {carSeat}
                              </span>
                              <button
                                  type="button"
                                  className="btn btn-outline-secondary rounded-circle"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    border: "1px solid #d70015",
                                    color: "#d70015",
                                  }}
                                  onClick={() => handleCarSeatChange("increment")}
                              >
                                  <i className="fa fa-plus" style={{ fontSize: "0.8rem" }}></i>
                              </button>
                          </div>
                      </div>
                  </div>



              </div>

            </div>
          </div>
        </section>

        {/* Pickup DateTime Picker Modal */}
        {showPickupDateTimePicker && (
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 2100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => setShowPickupDateTimePicker(false)}
          >
            <div
              className="bg-white rounded shadow-lg p-4"
              style={{
                maxWidth: "400px",
                width: "100%",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h5 className="mb-3" style={{ color: "#d70015" }}>
                Select Flight Arrival Date &amp; Time
              </h5>
              <div className="d-flex gap-2 mb-3">
                <input
                  type="date"
                  value={pickupTempDate}
                  onChange={(e) => setPickupTempDate(e.target.value)}
                  className="form-control border"
                />
                <select
                  value={pickupTempTime}
                  onChange={(e) => setPickupTempTime(e.target.value)}
                  className="form-control border"
                >
                  <option value="">Select time</option>
                  {generateTimeOptions().map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPickupDateTimePicker(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ backgroundColor: "#d70015", borderColor: "#d70015" }}
                  onClick={() => {
                    if (!pickupTempDate || !pickupTempTime) {
                      return;
                    }
                    const now = new Date();
                    const pickupDateTime = buildDateFromParts(pickupTempDate, pickupTempTime);
                    if (!pickupDateTime || pickupDateTime < now) {
                      setValidationError("Pickup date/time cannot be in the past.");
                      return;
                    }
                    handleDateTimeChange("flightArrival", pickupTempDate, pickupTempTime);
                    setShowPickupDateTimePicker(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Return DateTime Picker Modal */}
        {showReturnDateTimePicker && (
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 2100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => setShowReturnDateTimePicker(false)}
          >
            <div
              className="bg-white rounded shadow-lg p-4"
              style={{
                maxWidth: "400px",
                width: "100%",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h5 className="mb-3" style={{ color: "#d70015" }}>
                Select Return Date &amp; Time
              </h5>
              <div className="d-flex gap-2 mb-3">
                <input
                  type="date"
                  value={returnTempDate}
                  onChange={(e) => setReturnTempDate(e.target.value)}
                  className="form-control border"
                />
                <select
                  value={returnTempTime}
                  onChange={(e) => setReturnTempTime(e.target.value)}
                  className="form-control border"
                >
                  <option value="">Select time</option>
                  {generateTimeOptions().map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReturnDateTimePicker(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ backgroundColor: "#d70015", borderColor: "#d70015" }}
                  onClick={() => {
                    if (!returnTempDate || !returnTempTime) {
                      return;
                    }
                    const now = new Date();
                    const returnDateTime = buildDateFromParts(returnTempDate, returnTempTime);
                    const pickupDateTime = formData.flightArrival
                      ? new Date(formData.flightArrival)
                      : null;
                    if (!returnDateTime || returnDateTime < now) {
                      setValidationError("Return date/time cannot be in the past.");
                      return;
                    }
                    if (pickupDateTime && returnDateTime <= pickupDateTime) {
                      setValidationError("Return date/time must be after pickup date/time.");
                      return;
                    }
                    handleDateTimeChange("returnDate", returnTempDate, returnTempTime);
                    setShowReturnDateTimePicker(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        <SeoLinksSection />
        <Footer />
      </>
    </>
  );
}
